import _ from 'lodash';

import { ACCOUNT_APPLIED_TYPES, FetchAccounts } from 'assets/js/api/accounts';
import { ACTIVE_PLANS } from 'assets/js/plans';
import { FetchUsers } from 'assets/js/api/users';
import { Get, Params, Post, urls } from 'assets/js/api';

export const SITE_WEB_LINK_TYPES = {
  SITE_WEB_LINK_SITE_WIDE: 'site_web_link_site_wide',
  SITE_WEB_LINK_FOR_SITE_USER: 'site_web_link_for_site_user',
};

export const SITE_CACHE_FIELDS = {
  RESERVABLES: 'reservables',
};

export const SITE_USER_PERMISSIONS = {
  ADMIN_FORM_FIELDS: 'admin_form_fields',
  FORCE: 'force',
  FORCE_NOT_OWN: 'force_not_own',
  INVOICES: 'invoices',
  MAINTENANCE: 'maintenance',
  MAINTENANCE_NOT_OWN: 'maintenance_not_own',
  RESERVATIONS: 'reservations',
  RESERVATIONS_NOT_OWN: 'reservations_not_own',
  SKIP_REQUIRED_FORM_FIELDS: 'skip_required_form_fields',
  TEMPLATES: 'templates',
  TEMPLATES_NOT_OWN: 'templates_not_own',
  USERS: 'users',
};

export const SITE_USER_PERMISSION_VALUES = {
  AVAILABLE: 'available',
  NOT_AVAILABLE: 'not_available',
  READ: 'read',
  READ_WRITE: 'readwrite',
};

// SiteUserHasPermissionFor is used to figure out whether or not a use has a
// permission for some minor site role.
// The permissionsObj is the object normally found in the site as_dict.
// The permission is defined above in SITE_USER_PERMISSIONS.
// The expectation is defined above in SITE_USER_PERMISSION_VALUES.
// The function returns a bool.
export const SiteUserHasPermissionFor = (
  permissionsObj,
  permission,
  expectation,
) => {
  if (!_.isPlainObject(permissionsObj)) {
    throw Error(`Unknown permissions object '${permissionsObj}'`);
  }
  if (!_.includes(_.values(SITE_USER_PERMISSIONS), permission)) {
    throw Error(`Unknown permission '${permission}'`);
  }
  if (!_.includes(_.values(SITE_USER_PERMISSION_VALUES), expectation)) {
    throw Error(`Unknown expectation '${expectation}'`);
  }

  return _.get(permissionsObj, permission, '') === expectation;
};

// FetchTags fetches tags for a site and stores returns either the users or
// the total count of tags. It gives you options for how to handle both error
// and loading states, and is dependent on the context of the Vue component.
export const FetchTags = async (ctx, params, siteId) => {
  const extraSleep = params.extraSleep; // Sleep before fetch (milliseconds)
  const str = params.str; // Search string
  const returnTags = params.returnTags; // Return tags or count
  const localLoading = params.localLoading; // Set local loading state (ctx)
  const emitLoading = params.emitLoading; // Emit loading state (ctx)
  const localError = params.localError; // Set local error state (ctx)
  const emitError = params.emitError; // Emit error state (ctx)
  const throwError = params.throwError; // Throw errors if any are caught

  if (_.isUndefined(siteId)) throw Error('FetchTags: siteId undefined');

  if (extraSleep) {
    await ctx.sleep(extraSleep);
  }

  // For local fetches for components, don't set the loading state
  // because the component itself should handle that.
  if (!returnTags) {
    if (emitLoading) ctx.$emit('emitLoading', true);
    if (localLoading) ctx.loading = true;
  }

  let tags;
  const opts = {
    params: { site_id: siteId },
  };
  if (str) opts.params.query = str;
  try {
    tags = await Get(urls.TAGS_NO_PARAMS, opts);
  } catch (err) {
    if (localLoading) ctx.loading = false;
    if (emitLoading) ctx.$emit('emitLoading', false);
    if (throwError) throw err;
    if (localError) ctx.err = err;
    if (emitError) ctx.$emit('emitErr', err);
    return null;
  }

  const sortedTags = _.sortBy(tags.data, (t) => t.name);

  if (returnTags) return sortedTags;

  ctx.tags = sortedTags;
  if (localLoading) ctx.loading = false;
  if (emitLoading) ctx.$emit('emitLoading', false);
  return tags.numitems;
};

export async function GetAccountsUser({
  accountType = ACCOUNT_APPLIED_TYPES.USER_SITE,
  childrenEnd = null,
  childrenStart = null,
  portalId = null,
  siteId = null,
  userId = null,
  when = null,
}) {
  if (_.isNull(siteId) && _.isNull(portalId)) {
    throw Error('no siteId or portalId specified');
  }
  if (_.isNull(userId)) throw Error('no userId specified');

  let response;
  try {
    response = await FetchAccounts(_.pickBy({
      accountType,
      childrenEnd,
      childrenStart,
      portalId,
      siteId,
      userId,
      verbose: false,
      when,
    }, (val) => !_.isNil(val) && val !== ''));
  } catch (err) {
    // A 404 signals the user doesn't have access to something
    // which can be a valid state when you can view a calendar
    // but not use it.
    if (err.name === 'StatusCode404') {
      return [];
    }
    throw err;
  }

  return response.data;
}

export async function GetPaymentMethods(siteId) {
  const response = await Get(urls.STRIPE_PAYMENT_METHODS(siteId));

  return response.data;
}

export async function GetPortals(siteId) {
  const response = await Get(urls.SITE_PORTALS(siteId));

  return response.data;
}

export async function GetReservablesForBooking(siteId, {
  reservableType = null,
} = {}) {
  const opts = {
    params: {
      any_active: true,
      attribs: Params.RESERVATION_BOOK_ATTRIBS,
      sort_atr: 'name',
      site_id: siteId,
    },
  };
  if (!_.isNil(reservableType)) opts.params.reservable_type = reservableType;

  const URL = urls.RESERVABLES;
  const response = await Get(URL, opts);
  return response.data;
}

export async function GetReservationForms(siteId) {
  const response = await Get(
    urls.SITE_ATTACHED_RESERVATION_FORMS_ALL(siteId));

  return response.data;
}

export async function GetSite(siteId) {
  const response = await Get(urls.SITE(siteId));

  return response.data;
}

export async function GetUsersSlim(siteId) {
  const siteUsers = await FetchUsers(this,
    {
      fast: true,
      limit: 99999999,
      includeBarcode: true,
      offset: 0,
      returnUsers: true,
      throwError: true,
    },
    siteId);

  return siteUsers;
}

// HasMissingRequireProperties gets all of the site properties for a site
// and compares the required values to the values a provided siteUser has
// to determine if there are any missing properties.  If true, a user will
// typically be re-directed to the site page to rectify the situation.
export async function HasMissingRequiredProperties(siteWithUser) {
  // With no site user we assume nothing missing since there is nothing
  // missing technically and other factors will prevent them from booking.
  if (!siteWithUser
    || !siteWithUser.role
    || siteWithUser.role === 'non-member') {
    return false;
  }

  // First check the name field.
  if (siteWithUser.require_first_last_name &&
    (!siteWithUser.user_display_name ||
      siteWithUser.user_display_name.indexOf(' ') === -1)) {
    return true;
  }

  // Check the accounts field.
  if (siteWithUser && siteWithUser.require_account_numbers &&
    siteWithUser.user_account_numbers &&
    siteWithUser.user_account_numbers.length === 0) {
    return true;
  }

  // Pulldown site user properties
  const url = urls.SITE_USER_PROPERTY_DEFINITIONS(siteWithUser.site_id);

  const findSiteUserProperty = (siteUserPropdefs, propdef) => {
    const foundProp = _.find(siteUserPropdefs, (prop) =>
      prop.propertydef.propdef_id === propdef.propdef_id);

    if (foundProp) return foundProp.value;
    return '';
  };

  try {
    const resp = await Get(url);
    const siteUserPropdefs = resp.data;

    let missing = false;
    _.forEach(siteUserPropdefs, (propdef) => {
      if (propdef.property_definition.required
        && findSiteUserProperty(siteWithUser.site_user_properties,
          propdef) === '') {
        missing = true;
        return false; // Abort forEach.
      }
      return true; // Keep checking.
    });
    return missing;
  } catch (err) {
    throw Error(err);
  }
}

export const StartTrial = async (siteId) => {
  const opts = {
    plan_code: ACTIVE_PLANS.premium,
  };

  const resp = await Post(urls.SITE_PLAN_PREMIUM_TRIAL(siteId), opts);

  if (resp) return resp;
  return null;
};

// UpdateContacts updates the primary and secondary contacts for a site.
// Primary contacts can not be changed. Secondary contacts can be deleted by
// pruning the ones we wish to remove from the known list of secondary contacts
// for this site.
//
// params is in the form:
// {
//   siteId: site ID, string
//   contactSecondaryIDs: [ user ID ], array of strings
// }
export const UpdateContacts = async (params) => {
  const siteId = params.siteId;
  // The site update endpoint needs [{ user_id: xxx }].
  const secondary = params.contactSecondaryIds
    .map((id) => ({ user_id: id }));

  if (_.isUndefined(siteId)) {
    throw Error('UpdateContacts: siteId undefined');
  }
  if (_.isUndefined(secondary)) {
    throw Error('UpdateContacts: contactSecondaryIds undefined');
  }

  const opts = {
    secondary_contacts: secondary,
  };

  const resp = await Post(urls.SITE_UPDATE(siteId), opts);

  if (resp) return resp;
  return null;
};

// GetCacheNonce checks the cache nonce value for a given field. If the field
// is unknown or not yet supported we return null.
export const GetCacheNonce = async (siteId, field) => {
  if (field === SITE_CACHE_FIELDS.RESERVABLES) {
    try {
      const url = urls.SITE_CACHE_NONCE_RESERVABLES(siteId);
      const resp = await Get(url);
      return resp;
    } catch (err) {
      return null;
    }
  }
  return null;
};

// ReservablesCacheRequiresRefresh determines if the list of reservables needs
// to be pulled from the backend or not. In the case that for this site the
// list of reservables has never been pulled, the most recent nonce is stored
// and the function returns true, indicating that the list of reservables
// should be fetched. Otherwise, it compares the latest nonce from the backend
// to the current nonce stored in the frontend and indicates if a refetch is
// necessary.
export const ReservablesCacheRequiresRefresh = async (ctx, siteId) => {
  // To prevent flooding of the API we cache this response for 5 seconds.
  // We simply use a global variable on window to do this.

  // Check if we're in the window of the last check.
  const lastCheck = ctx?.store?.state?.reservables?.lastCheck || 0;
  if (+new Date() - lastCheck < 2000) {
    // If less than 2000ms (2s) we don't check again.
    // This was implement for pages that do a lot of checks like subscriptions
    // that can create many instances of ReservationViewWrapper all at once
    // and we ended up with tons and tons of calls all at once.
    return false;
  }

  // Always grab the nonce as we'll be using it later regardless.
  const nonce = await GetCacheNonce(siteId, SITE_CACHE_FIELDS.RESERVABLES);
  const oldNonce = ctx?.store?.state?.reservables?.nonceBySite[siteId];

  // Store our last check.
  await ctx.store.dispatch('StoreReservablesCacheNonceLastCheck', +new Date());

  // Store the new nonce if it is new.
  if (nonce !== oldNonce) {
    await ctx.store.dispatch('StoreReservablesCacheNonceForSite',
      { siteId, nonce },
    );
    await ctx.store.dispatch('ClearNamedUnitsCacheForSite',
      { siteId },
    );
    return true;
  }
  return false;
};
